import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Tooltip, Alert, FormControlLabel, Checkbox,
  Chip, IconButton, Stack, Grid, Tabs, Tab, CircularProgress
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CalendarMonth as CalendarIcon, Lock as LockIcon, CheckCircle as CheckCircleIcon,
  Add as AddIcon, ChevronLeft as PrevIcon,
  ChevronRight as NextIcon, Today as TodayIcon, Delete as DeleteIcon,
  FilterList as FilterIcon, ViewDay as DayViewIcon, ViewWeek as WeekViewIcon,
  CalendarViewMonth as MonthViewIcon, AccessTime as TimeIcon,
  MeetingRoom as RoomIcon, Groups as GroupsIcon, Person as TeacherIcon,
  Close as CloseIcon, Print as PrintIcon, Edit as EditIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { academyStore, generateTimeSlots, getOrderedDaysOfWeek } from '../services/academyStore';
import { academyApi } from '../services/academyApi';
import { Session, Room, Teacher, Group } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';

type GroupByMode = 'byRoom' | 'byGroup' | 'byTeacher';

// Safe Local Date Formatter (Prevents UTC timezone shifts)
const formatDateLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDayOfWeekFromStr = (dateStr: string): number => {
  const parts = dateStr.split('-');
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  return new Date(y, m, d).getDay();
};

const minutesToHHmm = (totalMin: number): string => {
  const clamped = Math.max(0, totalMin);
  const h = Math.floor(clamped / 60) % 24;
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const timeToMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// boubaker 22/08/2026 - onglet planning : chaque onglet porte ses propres filtres/dates/données
interface PlannerTab {
  id: number;
  label: string;
  viewMode: 'day' | 'week' | 'month';
  groupBy: GroupByMode;
  dateStr: string;   // ancre jour/semaine
  monthStr: string;  // ancre mois (YYYY-MM)
  startDate: string; // filtre chargement : début
  endDate: string;   // filtre chargement : fin
  filterRoomId: number | 'all';
  filterTeacherId: number | 'all';
  filterGroupId: number | 'all';
  sessions: Session[];
  loaded: boolean;
  loading: boolean;
}

// boubaker 22/08/2026 - bornes de période par défaut selon le mode d'affichage
const computePeriodRange = (viewMode: 'day' | 'week' | 'month', dateStr: string, monthStr: string, firstDay: number): { startDate: string; endDate: string } => {
  const parts = dateStr.split('-').map(Number);
  const cur = new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1);
  if (viewMode === 'day') return { startDate: dateStr, endDate: dateStr };
  if (viewMode === 'week') {
    const diff = (cur.getDay() - firstDay + 7) % 7;
    const start = new Date(cur);
    start.setDate(cur.getDate() - diff);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { startDate: formatDateLocal(start), endDate: formatDateLocal(end) };
  }
  const [y, m] = monthStr.split('-').map(Number);
  const monthStart = new Date(y, (m || 1) - 1, 1);
  const monthEnd = new Date(y, m || 1, 0);
  return { startDate: formatDateLocal(monthStart), endDate: formatDateLocal(monthEnd) };
};

export const SchedulerPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useLanguage();
  const { user } = useAuth();
  const [, setTick] = useState(0);

  // Agency ID
  const rawAgency = user?.agency;
  const parsedDosId = rawAgency ? parseInt(String(rawAgency), 10) : NaN;
  const connectedDosId = (!isNaN(parsedDosId) && parsedDosId > 0) ? parsedDosId : 1;

  // Parameters from Academy Settings
  const pStartTime = academyStore.getPlanningStartTime(connectedDosId);
  const pEndTime = academyStore.getPlanningEndTime(connectedDosId);
  const pSlotDuration = academyStore.getPlanningSlotDurationMinutes(connectedDosId);
  const pFirstDay = academyStore.getPlanningFirstDayOfWeek(connectedDosId);
  const pDefaultView = academyStore.getPlanningDefaultView(connectedDosId);
  const pWeekendDays = academyStore.getPlanningWeekendDays(connectedDosId);
  const pWeekendBgColor = academyStore.getPlanningWeekendBgColor(connectedDosId);

  // Dynamic Time Slots & Ordered Days
  const timeSlots = generateTimeSlots(pStartTime, pEndTime, pSlotDuration);

  const todayStr = formatDateLocal(new Date());

  // Clôture de l'agence (DOS_CLOSEDPLNG)
  const closedDateStr = user?.dosClosedPlng ? user.dosClosedPlng.split('T')[0] : '';
  const isDateClosed = (dateStr?: string): boolean => {
    if (!closedDateStr || !dateStr) return false;
    return dateStr <= closedDateStr;
  };

  // boubaker 22/08/2026 - fabrique d'onglet : dates par défaut = période courante
  const makeTab = (id: number): PlannerTab => {
    const range = computePeriodRange(pDefaultView, todayStr, todayStr.substring(0, 7), pFirstDay);
    return {
      id,
      label: `Planning ${id}`,
      viewMode: pDefaultView,
      groupBy: 'byRoom',
      dateStr: todayStr,
      monthStr: todayStr.substring(0, 7),
      startDate: range.startDate,
      endDate: range.endDate,
      filterRoomId: 'all',
      filterTeacherId: 'all',
      filterGroupId: 'all',
      sessions: [],
      loaded: false,
      loading: false
    };
  };

  // ── MULTI-TABS STATE ──
  const [tabs, setTabs] = useState<PlannerTab[]>(() => [makeTab(1)]);
  const [activeTabId, setActiveTabId] = useState<number>(1);
  const nextTabIdRef = useRef<number>(2);
  const tabsRef = useRef<PlannerTab[]>(tabs);
  useEffect(() => { tabsRef.current = tabs; }, [tabs]);

  const activeTab: PlannerTab | null = tabs.find(tb => tb.id === activeTabId) || null;

  // Modals & Evaluation State
  const [openModal, setOpenModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editRoomId, setEditRoomId] = useState(1);
  const [editTeacherId, setEditTeacherId] = useState(1);
  const [editGroupId, setEditGroupId] = useState(1);
  const [editDate, setEditDate] = useState('');
  const [editStart, setEditStart] = useState('09:00');
  const [editEnd, setEditEnd] = useState('10:30');
  const [editStatus, setEditStatus] = useState<string>('Scheduled');
  const [editStatusReason, setEditStatusReason] = useState<string>('');
  const [editPostponedDate, setEditPostponedDate] = useState<string>('');
  const [editPostponedTime, setEditPostponedTime] = useState<string>('');
  const [editTeacherAbsent, setEditTeacherAbsent] = useState<boolean>(false);

  // boubaker 22/08/2026 - contrôles planning serveurs : le frontend n'affiche
  // que les messages de rejet renvoyés par l'API.
  const [controlErrors, setControlErrors] = useState<{ code: string; lib: string }[]>([]);
  const [editControlErrors, setEditControlErrors] = useState<{ code: string; lib: string }[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [actionSuccessMsg, setActionSuccessMsg] = useState<string | null>(null);
  const [dropErrorMsg, setDropErrorMsg] = useState<string | null>(null);

  // Form State for new session
  const [selectedRoomId, setSelectedRoomId] = useState<number>(1);
  const [selectedTeacherId, setSelectedTeacherId] = useState<number>(1);
  const [selectedGroupId, setSelectedGroupId] = useState<number>(1);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:30');
  const [isRecurring, setIsRecurring] = useState(true);
  const [repeatWeeks, setRepeatWeeks] = useState(4);
  // Date cible de la séance en cours de création (cellule cliquée sinon onglet actif)
  const [createTargetDate, setCreateTargetDate] = useState<string>(todayStr);

  // ── PRINT POPUP STATE ──
  // boubaker 22/08/2026 - impression : liste plate (Jour/Début/Fin/Salle/Enseignant/Groupe)
  const [printOpen, setPrintOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printLoaded, setPrintLoaded] = useState(false);
  const [printSessions, setPrintSessions] = useState<Session[]>([]);
  const [printStartDate, setPrintStartDate] = useState<string>(todayStr);
  const [printEndDate, setPrintEndDate] = useState<string>(todayStr);
  const [printRoomId, setPrintRoomId] = useState<number | 'all'>('all');
  const [printTeacherId, setPrintTeacherId] = useState<number | 'all'>('all');
  const [printGroupId, setPrintGroupId] = useState<number | 'all'>('all');

  useEffect(() => {
    academyStore.ensureSessionsLoaded();
    academyStore.ensureRoomsLoaded();
    academyStore.ensureRoomRentalsLoaded();
    academyStore.ensureTeachersLoaded();
    academyStore.ensureGroupsLoaded();
    return academyStore.subscribe(() => setTick(t => t + 1));
  }, []);

  // boubaker 22/08/2026 - pont d'événements depuis la matrice (helper hors composant)
  useEffect(() => {
    const onOpenDetails = (e: Event) => handleOpenSessionDetails((e as CustomEvent).detail as Session);
    const onOpenAdd = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      handleOpenAddSession(d.roomId, d.teacherId, d.groupId, d.slotStart, d.slotEnd, d.targetDate);
    };
    window.addEventListener('blobtic-open-session-details', onOpenDetails);
    window.addEventListener('blobtic-open-add-session', onOpenAdd);
    return () => {
      window.removeEventListener('blobtic-open-session-details', onOpenDetails);
      window.removeEventListener('blobtic-open-add-session', onOpenAdd);
    };
  }, []);

  const rooms = academyStore.getRooms();
  const roomRentals = academyStore.getRoomRentals();
  const teachers = academyStore.getTeachers();
  const groups = academyStore.getGroups();
  const groupStudents = academyStore.getGroupStudents();

  // ── TAB DATA LOADING (serveur direct, sans cache) ──
  const loadTabData = async (tabId: number, overrides?: Partial<PlannerTab>) => {
    const current = tabsRef.current.find(tb => tb.id === tabId);
    if (!current) return;
    const merged: PlannerTab = { ...current, ...overrides };

    setTabs(prev => prev.map(tb => tb.id === tabId ? { ...merged, loading: true } : tb));

    const result = await academyApi.getSessionsRange(
      merged.startDate,
      merged.endDate,
      { room_id: merged.filterRoomId, teacher_id: merged.filterTeacherId, group_id: merged.filterGroupId }
    );

    setTabs(prev => prev.map(tb => tb.id === tabId ? {
      ...tb,
      sessions: result || [],
      loaded: true,
      loading: false
    } : tb));
  };

  // ── TAB HANDLERS ──
  const handleAddTab = () => {
    const id = nextTabIdRef.current++;
    setTabs(prev => [...prev, makeTab(id)]);
    setActiveTabId(id);
  };

  const handleCloseTab = (tabId: number) => {
    setTabs(prev => {
      const idx = prev.findIndex(tb => tb.id === tabId);
      const next = prev.filter(tb => tb.id !== tabId);
      if (tabId === activeTabId && next.length > 0) {
        setActiveTabId(next[Math.max(0, idx - 1)].id);
      }
      return next;
    });
  };

  const handlePatchActiveTab = (patch: Partial<PlannerTab>, autoReload = false) => {
    if (!activeTab) return;
    const merged = { ...activeTab, ...patch };
    setTabs(prev => prev.map(tb => tb.id === merged.id ? merged : tb));
    if (autoReload && (merged.loaded || patch.loaded)) {
      loadTabData(merged.id, patch);
    }
  };

  // Navigation Handlers (recalculent la plage de chargement puis rechargent)
  const handlePrevPeriod = () => {
    if (!activeTab) return;
    if (activeTab.viewMode === 'day') {
      const p = activeTab.dateStr.split('-').map(Number);
      const d = new Date(p[0], p[1] - 1, p[2]);
      d.setDate(d.getDate() - 1);
      const nds = formatDateLocal(d);
      handlePatchActiveTab({ dateStr: nds, ...computePeriodRange('day', nds, activeTab.monthStr, pFirstDay) }, true);
    } else if (activeTab.viewMode === 'week') {
      const p = activeTab.dateStr.split('-').map(Number);
      const d = new Date(p[0], p[1] - 1, p[2]);
      d.setDate(d.getDate() - 7);
      const nds = formatDateLocal(d);
      handlePatchActiveTab({ dateStr: nds, ...computePeriodRange('week', nds, activeTab.monthStr, pFirstDay) }, true);
    } else {
      const [y, m] = activeTab.monthStr.split('-').map(Number);
      const d = new Date(y, m - 2, 1);
      const nms = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      handlePatchActiveTab({ monthStr: nms, ...computePeriodRange('month', activeTab.dateStr, nms, pFirstDay) }, true);
    }
  };

  const handleNextPeriod = () => {
    if (!activeTab) return;
    if (activeTab.viewMode === 'day') {
      const p = activeTab.dateStr.split('-').map(Number);
      const d = new Date(p[0], p[1] - 1, p[2]);
      d.setDate(d.getDate() + 1);
      const nds = formatDateLocal(d);
      handlePatchActiveTab({ dateStr: nds, ...computePeriodRange('day', nds, activeTab.monthStr, pFirstDay) }, true);
    } else if (activeTab.viewMode === 'week') {
      const p = activeTab.dateStr.split('-').map(Number);
      const d = new Date(p[0], p[1] - 1, p[2]);
      d.setDate(d.getDate() + 7);
      const nds = formatDateLocal(d);
      handlePatchActiveTab({ dateStr: nds, ...computePeriodRange('week', nds, activeTab.monthStr, pFirstDay) }, true);
    } else {
      const [y, m] = activeTab.monthStr.split('-').map(Number);
      const d = new Date(y, m, 1);
      const nms = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      handlePatchActiveTab({ monthStr: nms, ...computePeriodRange('month', activeTab.dateStr, nms, pFirstDay) }, true);
    }
  };

  const handleToday = () => {
    handlePatchActiveTab({
      dateStr: todayStr,
      monthStr: todayStr.substring(0, 7),
      ...computePeriodRange(activeTab?.viewMode || 'week', todayStr, todayStr.substring(0, 7), pFirstDay)
    }, true);
  };

  const handleOpenAddSession = (roomId?: number, teacherId?: number, groupId?: number, slotStart?: string, slotEnd?: string, targetDate?: string) => {
    if (roomId) setSelectedRoomId(roomId);
    if (teacherId) setSelectedTeacherId(teacherId);
    if (groupId) setSelectedGroupId(groupId);
    if (slotStart) setStartTime(slotStart);
    if (slotEnd) setEndTime(slotEnd);
    setCreateTargetDate(targetDate || activeTab?.dateStr || todayStr);
    setControlErrors([]);
    setOpenModal(true);
  };

  const handleOpenSessionDetails = (session: Session) => {
    setSelectedSession(session);
    setEditMode(false);
    setEditRoomId(session.room_id);
    setEditTeacherId(session.teacher_id);
    setEditGroupId(session.group_id);
    setEditDate(session.date);
    setEditStart(session.start_time);
    setEditEnd(session.end_time);
    setEditStatus(session.status || 'Scheduled');
    setEditStatusReason(session.status_reason || '');
    setEditPostponedDate(session.postponed_date || '');
    setEditPostponedTime(session.postponed_time || '');
    setEditTeacherAbsent(Boolean(session.teacher_absent));
    setEditControlErrors([]);
    setOpenDetailsModal(true);
  };

  const handleDeleteSession = async () => {
    if (!selectedSession) return;
    if (isDateClosed(selectedSession.date)) {
      blobticMessage.error(t('common.messages.error'), t('academy.scheduler.error_closed_planning', closedDateStr));
      return;
    }
    // boubaker 23/08/2026 - confirmation via le composant générique animé
    const confirmed = await blobticMessage.confirm({
      title: t('common.confirm.title.delete'),
      message: t('academy.scheduler.confirm_cancel'),
      severity: 'warning',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    });
    if (!confirmed) return;
    const res = await academyStore.deleteSession(selectedSession.id);
    if (res.success) {
      setOpenDetailsModal(false);
      blobticMessage.success(t('common.messages.success'), t('academy.scheduler.msg_session_cancelled'));
      if (activeTab?.loaded) loadTabData(activeTab.id);
    }
  };

  // boubaker 22/08/2026 - édition séance sélectionnée depuis le planning
  // Les contrôles planning sont serveurs (Do_Before_Update) : rejet affiché tel quel.
  const handleUpdateSession = async () => {
    if (!selectedSession) return;

    if (isDateClosed(selectedSession.date) || isDateClosed(editDate)) {
      setEditControlErrors([{ code: 'CTL_CLOSED_PLANNING', lib: t('academy.scheduler.error_closed_planning', closedDateStr) }]);
      return;
    }

    // Client-side quick check: Room Rental conflict
    const conflictingRental = roomRentals.find(r => {
      if (r.arm_idf !== editRoomId || r.status === 'CANCELLED') return false;
      const targetStartIso = `${editDate}T${editStart}:00`;
      const targetEndIso = `${editDate}T${editEnd}:00`;
      return r.start_date < targetEndIso && r.end_date > targetStartIso;
    });
    if (conflictingRental) {
      const roomNum = rooms.find(rm => rm.id === editRoomId)?.room_number || `#${editRoomId}`;
      setEditControlErrors([{
        code: 'CTL_ROOM_RENTED',
        lib: `Salle ${roomNum} indisponible : la salle fait l'objet d'une location en cours pour "${conflictingRental.client_name}".`
      }]);
      return;
    }

    setSaveLoading(true);
    try {
      const duration = Math.max(15, timeToMinutes(editEnd) - timeToMinutes(editStart));
      const res = await academyStore.updateSession(selectedSession.id, {
        room_id: editRoomId,
        teacher_id: editTeacherId,
        group_id: editGroupId,
        date: editDate,
        start_time: editStart,
        end_time: editEnd,
        duration,
        status: editStatus,
        status_reason: editStatusReason,
        postponed_date: editStatus === 'Postponed' ? editPostponedDate : '',
        postponed_time: editStatus === 'Postponed' ? editPostponedTime : '',
        teacher_absent: editTeacherAbsent
      });
      if (!res.success) {
        setEditControlErrors([{ code: 'CTL_BACKEND_REJECT', lib: res.message || t('academy.scheduler.error.conflict') }]);
        return;
      }
      setOpenDetailsModal(false);
      setActionSuccessMsg(t('academy.scheduler.session_updated'));
      setTimeout(() => setActionSuccessMsg(null), 3000);
      if (activeTab?.loaded) loadTabData(activeTab.id);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSaveSession = async () => {
    setControlErrors([]);

    if (isDateClosed(createTargetDate)) {
      setControlErrors([{ code: 'CTL_CLOSED_PLANNING', lib: t('academy.scheduler.error_closed_planning', closedDateStr) }]);
      return;
    }

    // Client-side quick check: Room Rental conflict
    const conflictingRental = roomRentals.find(r => {
      if (r.arm_idf !== selectedRoomId || r.status === 'CANCELLED') return false;
      const targetStartIso = `${createTargetDate}T${startTime}:00`;
      const targetEndIso = `${createTargetDate}T${endTime}:00`;
      return r.start_date < targetEndIso && r.end_date > targetStartIso;
    });
    if (conflictingRental) {
      const roomNum = rooms.find(rm => rm.id === selectedRoomId)?.room_number || `#${selectedRoomId}`;
      setControlErrors([{
        code: 'CTL_ROOM_RENTED',
        lib: `Salle ${roomNum} indisponible : la salle fait l'objet d'une location en cours pour "${conflictingRental.client_name}".`
      }]);
      return;
    }

    const sessionPayload: Partial<Session> = {
      room_id: selectedRoomId,
      teacher_id: selectedTeacherId,
      group_id: selectedGroupId,
      day_of_week: getDayOfWeekFromStr(createTargetDate),
      start_time: startTime,
      end_time: endTime,
      date: createTargetDate,
      is_recurring: isRecurring,
      attendance_taken: false
    };

    setSaveLoading(true);

    try {
      // boubaker 22/08/2026 - duplication côté serveur : SSN_REPEAT_WEEKS = N → N lignes en base
      const res = await academyStore.addSession({
        ...(sessionPayload as Omit<Session, 'id'>),
        repeat_weeks: isRecurring ? Math.max(1, repeatWeeks) : 1
      });

      if (!res.success) {
        setControlErrors([{ code: 'CTL_BACKEND_REJECT', lib: res.message || t('academy.scheduler.error.conflict') }]);
        return;
      }

      setOpenModal(false);
      setActionSuccessMsg(isRecurring && repeatWeeks > 1
        ? `${repeatWeeks} séances planifiées avec succès.`
        : 'Séance planifiée avec succès.');
      setTimeout(() => setActionSuccessMsg(null), 3000);
      if (activeTab?.loaded) loadTabData(activeTab.id);
    } finally {
      setSaveLoading(false);
    }
  };

  // ── DRAG & DROP (boubaker 22/08/2026) ──
  // Semaine/Mois → dépose sur une colonne jour : change la DATE.
  // Jour → dépose sur une colonne créneau : change les HEURES (durée conservée).
  const handleCellDrop = async (e: React.DragEvent, target: { dateStr?: string; slotStart?: string; slotEnd?: string }) => {
    e.preventDefault();
    const sessionId = Number(e.dataTransfer.getData('text/plain'));
    if (!sessionId || !activeTab) return;
    const session = activeTab.sessions.find(s => s.id === sessionId);
    if (!session) return;

    if (isDateClosed(session.date) || (target.dateStr && isDateClosed(target.dateStr))) {
      setDropErrorMsg(t('academy.scheduler.error_closed_planning', closedDateStr));
      setTimeout(() => setDropErrorMsg(null), 5000);
      return;
    }

    let changes: Partial<Session> = {};
    if (target.dateStr && activeTab.viewMode !== 'day') {
      changes = { date: target.dateStr };
    } else if (target.slotStart && target.dateStr) {
      const duration = session.duration && session.duration > 0
        ? session.duration
        : Math.max(15, timeToMinutes(session.end_time) - timeToMinutes(session.start_time));
      changes = {
        date: target.dateStr,
        start_time: target.slotStart,
        end_time: minutesToHHmm(timeToMinutes(target.slotStart) + duration),
        duration
      };
    }

    if (Object.keys(changes).length === 0) return;

    // boubaker 22/08/2026 - contrôles planning serveurs : le rejet éventuel
    // remonte via res.message et s'affiche en erreur de dépôt.
    const res = await academyStore.updateSession(sessionId, changes);
    if (!res.success) {
      setDropErrorMsg(res.message || t('academy.scheduler.error.conflict'));
      setTimeout(() => setDropErrorMsg(null), 5000);
      return;
    }
    loadTabData(activeTab.id);
  };

  // ── PRINT HANDLERS ──
  const handleOpenPrint = () => {
    // boubaker 22/08/2026 - pré-remplir avec la plage de l'onglet actif
    setPrintStartDate(activeTab?.startDate || todayStr);
    setPrintEndDate(activeTab?.endDate || todayStr);
    setPrintRoomId(activeTab?.filterRoomId ?? 'all');
    setPrintTeacherId(activeTab?.filterTeacherId ?? 'all');
    setPrintGroupId(activeTab?.filterGroupId ?? 'all');
    setPrintLoaded(false);
    setPrintSessions([]);
    setPrintOpen(true);
  };

  const handleLoadAndPreparePrint = async () => {
    setPrintLoading(true);
    try {
      const result = await academyApi.getSessionsRange(printStartDate, printEndDate, {
        room_id: printRoomId,
        teacher_id: printTeacherId,
        group_id: printGroupId
      });
      setPrintSessions(result || []);
      setPrintLoaded(true);
    } finally {
      setPrintLoading(false);
    }
  };

  // ── ENTITY ROWS PREPARATION (Rooms, Groups, or Teachers) ──
  const filteredRooms = rooms.filter(r => !activeTab || activeTab.filterRoomId === 'all' || r.id === activeTab.filterRoomId);
  const filteredGroups = groups.filter(g => !activeTab || activeTab.filterGroupId === 'all' || g.id === activeTab.filterGroupId);
  const filteredTeachers = teachers.filter(x => !activeTab || activeTab.filterTeacherId === 'all' || x.id === activeTab.filterTeacherId);

  // ── COLUMN DATES PREPARATION FOR ACTIVE TAB ──
  const tabDateParts = (activeTab?.dateStr || todayStr).split('-').map(Number);
  const currentDate = new Date(tabDateParts[0], (tabDateParts[1] || 1) - 1, tabDateParts[2] || 1);
  const selectedMonthAnchor = activeTab?.monthStr || todayStr.substring(0, 7);
  const [yStr, mStr] = selectedMonthAnchor.split('-');
  const monthYear = parseInt(yStr, 10) || currentDate.getFullYear();
  const monthIdx = (parseInt(mStr, 10) || (currentDate.getMonth() + 1)) - 1;
  const daysInMonthCount = new Date(monthYear, monthIdx + 1, 0).getDate();

  const buildDatesArray = (from: Date, count: number) => {
    const arr: { dateStr: string; dayNum: number; shortDayName: string }[] = [];
    for (let i = 0; i < count; i++) {
      const cur = new Date(from);
      cur.setDate(from.getDate() + i);
      const shortDayName = cur.toLocaleDateString('fr-FR', { weekday: 'short' }).toUpperCase().replace('.', '');
      arr.push({ dateStr: formatDateLocal(cur), dayNum: cur.getDate(), shortDayName });
    }
    return arr;
  };

  const monthDates = buildDatesArray(new Date(monthYear, monthIdx, 1), daysInMonthCount);

  const weekDates = (() => {
    const diff = (currentDate.getDay() - pFirstDay + 7) % 7;
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - diff);
    return buildDatesArray(weekStart, 7);
  })();

  // Top Left Header Label
  const getTopLeftHeader = () => {
    if (!activeTab) return '';
    if (activeTab.viewMode === 'month') {
      const d = new Date(monthYear, monthIdx, 1);
      return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    } else if (activeTab.viewMode === 'week') {
      return `Semaine du ${weekDates[0]?.dateStr}`;
    }
    return `${currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`;
  };

  const tabSessions = activeTab?.sessions || [];

  return (
    <GenericPage
      title={t('academy.scheduler.title')}
      icon={<CalendarIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {/* Date / Month Navigation Bar */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton onClick={handlePrevPeriod} size="small"><PrevIcon /></IconButton>

            {activeTab?.viewMode === 'month' ? (
              <TextField
                type="month"
                size="small"
                value={activeTab.monthStr}
                onChange={(e) => handlePatchActiveTab({ monthStr: e.target.value, ...computePeriodRange('month', activeTab.dateStr, e.target.value, pFirstDay) }, true)}
                sx={{ width: { xs: 130, sm: 150 } }}
              />
            ) : (
              <TextField
                type="date"
                size="small"
                value={activeTab?.dateStr || todayStr}
                onChange={(e) => {
                  if (!activeTab) return;
                  handlePatchActiveTab({ dateStr: e.target.value, ...computePeriodRange(activeTab.viewMode, e.target.value, activeTab.monthStr, pFirstDay) }, true);
                }}
                sx={{ width: { xs: 130, sm: 150 } }}
              />
            )}

            <IconButton onClick={handleNextPeriod} size="small"><NextIcon /></IconButton>
            <Button size="small" variant="outlined" startIcon={<TodayIcon />} onClick={handleToday} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
              {t('academy.scheduler.today')}
            </Button>
          </Stack>

          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenAddSession()} sx={{ borderRadius: 2 }}>
            {t('academy.scheduler.schedule_btn')}
          </Button>

          <Button variant="outlined" color="secondary" startIcon={<PrintIcon />} onClick={handleOpenPrint} sx={{ borderRadius: 2 }}>
            {t('academy.scheduler.print')}
          </Button>
        </Stack>
      }
    >
      <Box sx={{ pb: 4 }}>
        {/* CSS impression : seul #printable-planning est imprimé (même pattern que factures) */}
        <style>
          {`
            @media print {
              body * { visibility: hidden; }
              .MuiDialog-root, .MuiDialog-root * { visibility: visible; }
              .MuiDialogActions-root, .MuiDialogTitle-root, button, .no-print, .MuiIconButton-root {
                display: none !important;
                visibility: hidden !important;
              }
              .MuiDialog-paper {
                box-shadow: none !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                border: none !important;
              }
              .MuiDialogContent-root { padding: 8px !important; overflow: visible !important; }
              #printable-planning { visibility: visible !important; width: 100% !important; }
              #printable-planning table { width: 100% !important; border-collapse: collapse !important; }
              #printable-planning .print-sessions-table { font-size: 10px !important; }
              #printable-planning .print-sessions-table th,
              #printable-planning .print-sessions-table td {
                border: 1px solid #999 !important;
                color: #000 !important;
                background: #fff !important;
                padding: 3px 5px !important;
              }
            }
          `}
        </style>

        {/* Action Notification Banner */}
        {actionSuccessMsg && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: 2 }}>
            {actionSuccessMsg}
          </Alert>
        )}

        {dropErrorMsg && (
          <Alert severity="error" icon={<LockIcon />} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setDropErrorMsg(null)}>
            {dropErrorMsg}
          </Alert>
        )}

        {/* ── ONGLETS MULTI-PLANNINGS ── */}
        {tabs.length > 0 ? (
          <Paper elevation={1} sx={{ mb: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Tabs
              value={activeTabId}
              onChange={(_, v) => { if (typeof v === 'number') setActiveTabId(v); }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}
            >
              {tabs.map(tb => (
                <Tab
                  key={tb.id}
                  value={tb.id}
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{tb.label}</span>
                      <Box
                        component="span"
                        onClick={(ev) => { ev.stopPropagation(); handleCloseTab(tb.id); }}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', borderRadius: '50%',
                          '&:hover': { bgcolor: 'action.hover' }, ml: 0.5
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Stack>
                  }
                  sx={{ textTransform: 'none' }}
                />
              ))}
              <Tab
                key="__add__"
                value="__add__"
                onClick={handleAddTab}
                icon={<AddIcon />}
                aria-label={t('academy.scheduler.tab_new')}
                sx={{ minWidth: 42 }}
              />
            </Tabs>
          </Paper>
        ) : (
          <Paper elevation={1} sx={{ p: 4, mb: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Aucun onglet ouvert.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddTab}>
              {t('academy.scheduler.tab_new')}
            </Button>
          </Paper>
        )}

        {activeTab && (
          <>
            {/* Body Toolbar Container: Filtres de l'onglet actif + bouton Charger (sans cache serveur) */}
            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: 'background.paper' }}>
              <Grid container spacing={2} alignItems="center">
                {/* 1. Date de début (filtre) */}
                <Grid item xs={12} sm={6} md={1.7}>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    label={t('academy.scheduler.start_date')}
                    value={activeTab.startDate}
                    onChange={(e) => handlePatchActiveTab({ startDate: e.target.value }, true)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* 2. Date de fin (filtre) */}
                <Grid item xs={12} sm={6} md={1.7}>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    label={t('academy.scheduler.end_date')}
                    value={activeTab.endDate}
                    onChange={(e) => handlePatchActiveTab({ endDate: e.target.value }, true)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* 3. Mode d'Affichage Dropdown (Jour, Semaine, Mois) */}
                <Grid item xs={12} sm={6} md={1.8}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('academy.parameters.default_view')}
                    value={activeTab.viewMode}
                    onChange={(e) => handlePatchActiveTab({ viewMode: e.target.value as 'day' | 'week' | 'month' })}
                    InputProps={{
                      startAdornment: activeTab.viewMode === 'day' ? <DayViewIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> : activeTab.viewMode === 'week' ? <WeekViewIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> : <MonthViewIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    }}
                  >
                    <MenuItem value="day">{t('academy.scheduler.view_day')}</MenuItem>
                    <MenuItem value="week">{t('academy.scheduler.view_week')}</MenuItem>
                    <MenuItem value="month">{t('academy.scheduler.view_month')}</MenuItem>
                  </TextField>
                </Grid>

                {/* 4. Groupement Dynamique par Entité Dropdown */}
                <Grid item xs={12} sm={6} md={1.8}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('academy.scheduler.group_by')}
                    value={activeTab.groupBy}
                    onChange={(e) => handlePatchActiveTab({ groupBy: e.target.value as GroupByMode })}
                    InputProps={{
                      startAdornment: activeTab.groupBy === 'byRoom' ? <RoomIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} /> : activeTab.groupBy === 'byGroup' ? <GroupsIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} /> : <TeacherIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
                    }}
                  >
                    <MenuItem value="byRoom">{t('academy.scheduler.group_by_room')}</MenuItem>
                    <MenuItem value="byGroup">{t('academy.scheduler.group_by_group')}</MenuItem>
                    <MenuItem value="byTeacher">{t('academy.scheduler.group_by_teacher')}</MenuItem>
                  </TextField>
                </Grid>

                {/* 5. Filter Room */}
                <Grid item xs={12} sm={4} md={1.7}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('academy.scheduler.group_by_room')}
                    value={activeTab.filterRoomId}
                    onChange={(e) => handlePatchActiveTab({ filterRoomId: e.target.value === 'all' ? 'all' : Number(e.target.value) }, true)}
                  >
                    <MenuItem value="all">{t('academy.scheduler.all_rooms')}</MenuItem>
                    {rooms.map(r => (
                      <MenuItem key={r.id} value={r.id}>{r.room_number}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* 6. Filter Teacher */}
                <Grid item xs={12} sm={4} md={1.8}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('academy.scheduler.group_by_teacher')}
                    value={activeTab.filterTeacherId}
                    onChange={(e) => handlePatchActiveTab({ filterTeacherId: e.target.value === 'all' ? 'all' : Number(e.target.value) }, true)}
                  >
                    <MenuItem value="all">{t('academy.scheduler.all_teachers')}</MenuItem>
                    {teachers.map(tt => (
                      <MenuItem key={tt.id} value={tt.id}>{tt.full_name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* 7. Filter Group */}
                <Grid item xs={12} sm={4} md={1.8}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('academy.scheduler.group_by_group')}
                    value={activeTab.filterGroupId}
                    onChange={(e) => handlePatchActiveTab({ filterGroupId: e.target.value === 'all' ? 'all' : Number(e.target.value) }, true)}
                  >
                    <MenuItem value="all">{t('academy.scheduler.all_groups')}</MenuItem>
                    {groups.map(g => (
                      <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* 8. Bouton Charger : requête serveur directe sans cache */}
                <Grid item xs={12} md={1.2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={activeTab.loading}
                    onClick={() => loadTabData(activeTab.id)}
                    sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                  >
                    {activeTab.loading ? <CircularProgress size={18} color="inherit" /> : <><FilterIcon fontSize="small" sx={{ mr: 0.5 }} />{t('academy.scheduler.load')}</>}
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {!activeTab.loaded ? (
              /* Onglet pas encore chargé : inviter à cliquer sur Charger */
              <Paper elevation={3} sx={{ p: 6, borderRadius: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                <CalendarIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('academy.scheduler.not_loaded')}
                </Typography>
                <Button variant="contained" startIcon={<FilterIcon />} onClick={() => loadTabData(activeTab.id)} sx={{ mt: 2 }}>
                  {t('academy.scheduler.load')}
                </Button>
              </Paper>
            ) : (
              /* ── MAIN SCHEDULER MATRIX (DARK MODE & TIMEZONE SAFE) ── */
              <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' }}>
                <Box sx={{ px: 2, pt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('academy.scheduler.drag_hint')}
                  </Typography>
                </Box>
                {/* boubaker 22/08/2026 - scroll vertical+horizontal : 1ère colonne et 1ère ligne figées */}
                <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 330px)', WebkitOverflowScrolling: 'touch' }}>
                  {/* boubaker 22/08/2026 - table-layout fixed : toutes les colonnes même largeur (avec ou sans séance) */}
                  <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, textAlign: 'center' }}>
                    <thead>
                      <tr style={{ backgroundColor: isDark ? alpha('#ffffff', 0.05) : '#f8fafc', borderBottom: '2px solid', borderColor: 'divider' }}>
                        {/* Top-Left Cell: Month & Year or Week / Day Header */}
                        <th style={{
                          position: 'sticky', top: 0, left: 0, zIndex: 4,
                          padding: 16, width: 160, textTransform: 'capitalize', fontSize: '0.9rem',
                          color: theme.palette.text.primary, fontWeight: 800,
                          backgroundColor: isDark ? theme.palette.background.paper : '#f8fafc',
                          borderRight: '1px solid ' + theme.palette.divider, textAlign: 'left'
                        }}>
                          {getTopLeftHeader()}
                        </th>

                        {/* COLUMNS DEPENDING ON VIEW MODE — MONTH */}
                        {activeTab.viewMode === 'month' && monthDates.map(d => {
                          const isClosed = isDateClosed(d.dateStr);
                          const isToday = d.dateStr === todayStr;
                          const dayOfWeek = getDayOfWeekFromStr(d.dateStr);
                          const isWeekend = pWeekendDays.includes(dayOfWeek);
                          const headerBg = isClosed
                            ? (isDark ? alpha('#64748b', 0.25) : '#e2e8f0')
                            : isToday
                              ? (isDark ? alpha(theme.palette.primary.main, 0.25) : '#dbeafe')
                              : isWeekend
                                ? (isDark ? alpha(pWeekendBgColor || '#f97316', 0.25) : pWeekendBgColor)
                                : 'transparent';
                          return (
                            <th key={d.dateStr} style={{
                              position: 'sticky', top: 0, zIndex: 3,
                              padding: 10, width: 95, fontSize: '0.8rem',
                              color: isClosed ? theme.palette.text.disabled : isToday ? theme.palette.primary.main : isWeekend ? theme.palette.warning.main : theme.palette.text.primary,
                              fontWeight: isClosed || isToday || isWeekend ? 800 : 700,
                              borderRight: '1px solid ' + theme.palette.divider,
                              backgroundColor: headerBg === 'transparent' ? (isDark ? theme.palette.background.paper : '#ffffff') : headerBg,
                              borderBottom: isClosed ? '3px solid #64748b' : isToday || isWeekend ? `3px solid ${isToday ? theme.palette.primary.main : theme.palette.warning.main}` : '1px solid ' + theme.palette.divider
                            }}>
                              <div>{d.shortDayName} {d.dayNum}</div>
                              {isClosed ? (
                                <Chip label={t('academy.scheduler.closed_chip') || 'Clôturé'} size="small" sx={{ height: 14, fontSize: '0.55rem', mt: 0.3, bgcolor: '#64748b', color: 'white', fontWeight: 'bold' }} />
                              ) : isToday ? (
                                <Chip label="Aujourd'hui" size="small" color="primary" sx={{ height: 14, fontSize: '0.55rem', mt: 0.3 }} />
                              ) : isWeekend ? (
                                <Chip label="WE" size="small" color="warning" sx={{ height: 14, fontSize: '0.55rem', mt: 0.3, fontWeight: 'bold' }} />
                              ) : null}
                            </th>
                          );
                        })}

                        {/* WEEK */}
                        {activeTab.viewMode === 'week' && weekDates.map(d => {
                          const isClosed = isDateClosed(d.dateStr);
                          const isToday = d.dateStr === todayStr;
                          const dayOfWeek = getDayOfWeekFromStr(d.dateStr);
                          const isWeekend = pWeekendDays.includes(dayOfWeek);
                          const headerBg = isClosed
                            ? (isDark ? alpha('#64748b', 0.25) : '#e2e8f0')
                            : isToday
                              ? (isDark ? alpha(theme.palette.primary.main, 0.25) : '#dbeafe')
                              : isWeekend
                                ? (isDark ? alpha(pWeekendBgColor || '#f97316', 0.25) : pWeekendBgColor)
                                : 'transparent';
                          return (
                            <th key={d.dateStr} style={{
                              position: 'sticky', top: 0, zIndex: 3,
                              padding: 10, width: 120, fontSize: '0.85rem',
                              color: isClosed ? theme.palette.text.disabled : isToday ? theme.palette.primary.main : isWeekend ? theme.palette.warning.main : theme.palette.text.primary,
                              fontWeight: isClosed || isToday || isWeekend ? 800 : 700,
                              borderRight: '1px solid ' + theme.palette.divider,
                              backgroundColor: headerBg === 'transparent' ? (isDark ? theme.palette.background.paper : '#ffffff') : headerBg,
                              borderBottom: isClosed ? '3px solid #64748b' : isToday || isWeekend ? `3px solid ${isToday ? theme.palette.primary.main : theme.palette.warning.main}` : '1px solid ' + theme.palette.divider
                            }}>
                              <div>{d.shortDayName} {d.dayNum}</div>
                              {isClosed ? (
                                <Chip label={t('academy.scheduler.closed_chip') || 'Clôturé'} size="small" sx={{ height: 16, fontSize: '0.6rem', mt: 0.3, bgcolor: '#64748b', color: 'white', fontWeight: 'bold' }} />
                              ) : isToday ? (
                                <Chip label="Aujourd'hui" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem', mt: 0.3 }} />
                              ) : isWeekend ? (
                                <Chip label="WE" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem', mt: 0.3, fontWeight: 'bold' }} />
                              ) : null}
                            </th>
                          );
                        })}

                        {/* DAY : une colonne par créneau horaire */}
                        {activeTab.viewMode === 'day' && timeSlots.map(slot => {
                          const isDayClosed = isDateClosed(activeTab.dateStr);
                          return (
                            <th key={slot.start} style={{
                              position: 'sticky', top: 0, zIndex: 3,
                              padding: 10, width: 130, fontSize: '0.85rem',
                              color: isDayClosed ? theme.palette.text.disabled : theme.palette.text.primary, fontWeight: 600,
                              borderRight: '1px solid ' + theme.palette.divider,
                              backgroundColor: isDayClosed ? (isDark ? alpha('#64748b', 0.25) : '#e2e8f0') : (isDark ? theme.palette.background.paper : '#ffffff'),
                              borderBottom: isDayClosed ? '3px solid #64748b' : '1px solid ' + theme.palette.divider
                            }}>
                              <div>{slot.label}</div>
                              {isDayClosed && <Chip label={t('academy.scheduler.closed_chip') || 'Clôturé'} size="small" sx={{ height: 14, fontSize: '0.55rem', mt: 0.3, bgcolor: '#64748b', color: 'white', fontWeight: 'bold' }} />}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>

                    <tbody>
                      {/* ROWS DEPENDING ON GROUP BY (byRoom, byGroup, byTeacher) */}
                      {activeTab.groupBy === 'byRoom' && filteredRooms.map(room => (
                        <tr key={room.id} style={{ borderBottom: '1px solid ' + theme.palette.divider }}>
                          <td style={{ position: 'sticky', left: 0, zIndex: 2, padding: 12, backgroundColor: isDark ? theme.palette.background.paper : '#f8fafc', borderRight: '1px solid ' + theme.palette.divider, textAlign: 'left' }}>
                            <Typography variant="subtitle2" fontWeight={800} color="text.primary">{room.room_number}</Typography>
                            <Typography variant="caption" color="text.secondary">Cap: {room.capacity} | {room.floor}</Typography>
                          </td>
                          {renderEntityCells({
                            entityType: 'room', entityId: room.id,
                            viewMode: activeTab.viewMode, monthDates, weekDates, timeSlots,
                            dateAnchor: activeTab.dateStr, sessions: tabSessions, groups, teachers, rooms, groupStudents,
                            pWeekendDays, pWeekendBgColor, closedDateStr, t, theme,
                            onDragStartSession: (ev, s) => ev.dataTransfer.setData('text/plain', String(s.id)),
                            onCellDrop: (ev, target) => handleCellDrop(ev, target)
                          })}
                        </tr>
                      ))}

                      {activeTab.groupBy === 'byGroup' && filteredGroups.map(group => (
                        <tr key={group.id} style={{ borderBottom: '1px solid ' + theme.palette.divider }}>
                          <td style={{ position: 'sticky', left: 0, zIndex: 2, padding: 12, backgroundColor: isDark ? theme.palette.background.paper : '#f8fafc', borderRight: '1px solid ' + theme.palette.divider, textAlign: 'left' }}>
                            <Typography variant="subtitle2" fontWeight={800} color="primary.main">{group.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {groupStudents.filter(gs => gs.group_id === group.id).length} Étudiants
                            </Typography>
                          </td>
                          {renderEntityCells({
                            entityType: 'group', entityId: group.id,
                            viewMode: activeTab.viewMode, monthDates, weekDates, timeSlots,
                            dateAnchor: activeTab.dateStr, sessions: tabSessions, groups, teachers, rooms, groupStudents,
                            pWeekendDays, pWeekendBgColor, closedDateStr, t, theme,
                            onDragStartSession: (ev, s) => ev.dataTransfer.setData('text/plain', String(s.id)),
                            onCellDrop: (ev, target) => handleCellDrop(ev, target)
                          })}
                        </tr>
                      ))}

                      {activeTab.groupBy === 'byTeacher' && filteredTeachers.map(teacher => (
                        <tr key={teacher.id} style={{ borderBottom: '1px solid ' + theme.palette.divider }}>
                          <td style={{ position: 'sticky', left: 0, zIndex: 2, padding: 12, backgroundColor: isDark ? theme.palette.background.paper : '#f8fafc', borderRight: '1px solid ' + theme.palette.divider, textAlign: 'left' }}>
                            <Typography variant="subtitle2" fontWeight={800} color="text.primary">{teacher.full_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{teacher.specialization || 'Professeur'}</Typography>
                          </td>
                          {renderEntityCells({
                            entityType: 'teacher', entityId: teacher.id,
                            viewMode: activeTab.viewMode, monthDates, weekDates, timeSlots,
                            dateAnchor: activeTab.dateStr, sessions: tabSessions, groups, teachers, rooms, groupStudents,
                            pWeekendDays, pWeekendBgColor, closedDateStr, t, theme,
                            onDragStartSession: (ev, s) => ev.dataTransfer.setData('text/plain', String(s.id)),
                            onCellDrop: (ev, target) => handleCellDrop(ev, target)
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Paper>
            )}
          </>
        )}

        {/* Session Details / Edit / Cancel Modal */}
        <Dialog open={openDetailsModal} onClose={() => setOpenDetailsModal(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('academy.scheduler.session_details')}</DialogTitle>
          <DialogContent dividers>
            {selectedSession && !editMode && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {isDateClosed(selectedSession.date) && (
                  <Alert severity="warning" icon={<LockIcon />} sx={{ borderRadius: 2 }}>
                    {t('academy.scheduler.closed_warning') || "Ce créneau fait partie d'une période clôturée. Modification et annulation impossibles."}
                  </Alert>
                )}
                <Typography variant="subtitle1" fontWeight="bold">
                  {groups.find(g => g.id === selectedSession.group_id)?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Enseignant :</strong> {teachers.find(tt => tt.id === selectedSession.teacher_id)?.full_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Salle :</strong> {rooms.find(r => r.id === selectedSession.room_id)?.room_number}
                </Typography>
                <Typography variant="body2">
                  <strong>Date & Horaire :</strong> {selectedSession.date} ({selectedSession.start_time} - {selectedSession.end_time})
                </Typography>
                <Typography variant="body2">
                  <strong>Durée :</strong> {selectedSession.duration || '-'} min
                </Typography>
                <Typography variant="body2">
                  <strong>Statut Présence :</strong> {selectedSession.attendance_taken ? 'Appel Effectué' : 'Non pris'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                  <Chip
                    label={t(`academy.scheduler.status.${(selectedSession.status || 'Scheduled').toLowerCase()}`)}
                    color={
                      selectedSession.status === 'Completed' ? 'success' :
                      selectedSession.status === 'Cancelled' ? 'error' :
                      selectedSession.status === 'Postponed' ? 'warning' : 'primary'
                    }
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                  {selectedSession.teacher_absent && (
                    <Chip
                      label={t('academy.scheduler.teacher_absent')}
                      color="error"
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
                {selectedSession.status === 'Postponed' && selectedSession.postponed_date && (
                  <Alert severity="warning" sx={{ py: 0.5, borderRadius: 2 }}>
                    {t('academy.scheduler.postponed_to', selectedSession.postponed_date, selectedSession.postponed_time || '')}
                  </Alert>
                )}
                {selectedSession.status_reason && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', bgcolor: 'action.hover', p: 1, borderRadius: 1.5 }}>
                    <strong>{t('academy.scheduler.status_reason')} :</strong> {selectedSession.status_reason}
                  </Typography>
                )}
              </Box>
            )}

            {selectedSession && editMode && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {/* boubaker 22/08/2026 - rejets des contrôles planning serveurs */}
                {editControlErrors.map((err, idx) => (
                  <Alert key={idx} severity="error" icon={<LockIcon />} sx={{ borderRadius: 2 }}>
                    <strong>[{err.code}]</strong> {err.lib}
                  </Alert>
                ))}

                <TextField select label={t('academy.scheduler.group_by_room')} value={editRoomId} onChange={(e) => setEditRoomId(Number(e.target.value))} fullWidth required>
                  {rooms.map(r => (
                    <MenuItem key={r.id} value={r.id}>{r.room_number} (Cap: {r.capacity})</MenuItem>
                  ))}
                </TextField>
                <TextField select label={t('academy.scheduler.group_by_group')} value={editGroupId} onChange={(e) => setEditGroupId(Number(e.target.value))} fullWidth required>
                  {groups.map(g => (
                    <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                  ))}
                </TextField>
                <TextField select label={t('academy.scheduler.group_by_teacher')} value={editTeacherId} onChange={(e) => setEditTeacherId(Number(e.target.value))} fullWidth required>
                  {teachers.map(tt => (
                    <MenuItem key={tt.id} value={tt.id}>{tt.full_name}</MenuItem>
                  ))}
                </TextField>
                <TextField type="date" label={t('academy.scheduler.select_date')} value={editDate} onChange={(e) => setEditDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label={t('academy.scheduler.modal.start_time')} type="time" value={editStart} onChange={(e) => setEditStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />
                  <TextField label={t('academy.scheduler.modal.end_time')} type="time" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />
                </Box>

                <TextField
                  select
                  label={t('academy.scheduler.session_status')}
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="Scheduled">{t('academy.scheduler.status.scheduled')}</MenuItem>
                  <MenuItem value="Completed">{t('academy.scheduler.status.completed')}</MenuItem>
                  <MenuItem value="Postponed">{t('academy.scheduler.status.postponed')}</MenuItem>
                  <MenuItem value="Cancelled">{t('academy.scheduler.status.cancelled')}</MenuItem>
                </TextField>

                <TextField
                  label={t('academy.scheduler.status_reason')}
                  value={editStatusReason}
                  onChange={(e) => setEditStatusReason(e.target.value)}
                  fullWidth
                  placeholder="Ex: Enseignant absent, Férié..."
                />

                {editStatus === 'Postponed' && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      type="date"
                      label={t('academy.scheduler.postponed_date')}
                      value={editPostponedDate}
                      onChange={(e) => setEditPostponedDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      type="time"
                      label={t('academy.scheduler.postponed_time')}
                      value={editPostponedTime}
                      onChange={(e) => setEditPostponedTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Box>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editTeacherAbsent}
                      onChange={(e) => setEditTeacherAbsent(e.target.checked)}
                      color="error"
                    />
                  }
                  label={t('academy.scheduler.teacher_absent')}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            {!editMode ? (
              <>
                {!isDateClosed(selectedSession?.date) ? (
                  <Button color="error" startIcon={<DeleteIcon />} onClick={handleDeleteSession}>
                    {t('academy.scheduler.cancel_session')}
                  </Button>
                ) : <Box />}
                <Stack direction="row" spacing={1}>
                  {!isDateClosed(selectedSession?.date) && (
                    <Button startIcon={<EditIcon />} onClick={() => setEditMode(true)}>
                      {t('academy.scheduler.edit_session')}
                    </Button>
                  )}
                  <Button onClick={() => setOpenDetailsModal(false)} variant="contained">
                    Fermer
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Button onClick={() => { setEditMode(false); setEditControlErrors([]); }}>{t('btn.cancel')}</Button>
                <Button startIcon={<CheckCircleIcon />} onClick={handleUpdateSession} variant="contained" color="primary" disabled={saveLoading}>
                  {saveLoading ? <CircularProgress size={18} color="inherit" /> : t('academy.scheduler.save_changes')}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Schedule Session Modal with Rules Evaluation */}
        <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('academy.scheduler.schedule_btn')}</DialogTitle>
          <DialogContent dividers>

            {/* boubaker 22/08/2026 - rejets des contrôles planning serveurs */}
            {controlErrors.map((err, idx) => (
              <Alert key={idx} severity="error" icon={<LockIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                <strong>[{err.code}]</strong> {err.lib}
              </Alert>
            ))}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField select label={t('academy.scheduler.group_by_room')} value={selectedRoomId} onChange={(e) => setSelectedRoomId(Number(e.target.value))} fullWidth required>
                {rooms.map(r => (
                  <MenuItem key={r.id} value={r.id}>{r.room_number} (Cap: {r.capacity})</MenuItem>
                ))}
              </TextField>

              <TextField select label={t('academy.scheduler.group_by_group')} value={selectedGroupId} onChange={(e) => setSelectedGroupId(Number(e.target.value))} fullWidth required>
                {groups.map(g => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </TextField>

              <TextField select label={t('academy.scheduler.group_by_teacher')} value={selectedTeacherId} onChange={(e) => setSelectedTeacherId(Number(e.target.value))} fullWidth required>
                {teachers.map(tt => (
                  <MenuItem key={tt.id} value={tt.id}>{tt.full_name}</MenuItem>
                ))}
              </TextField>

              <TextField type="date" label={t('academy.scheduler.select_date')} value={createTargetDate} onChange={(e) => setCreateTargetDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Début" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />
                <TextField label="Fin" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth required />
              </Box>

              <FormControlLabel
                control={<Checkbox checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} />}
                label="Séance récurrente chaque semaine"
              />

              {isRecurring && (
                <TextField label="Nombre de semaines à répéter" type="number" value={repeatWeeks} onChange={(e) => setRepeatWeeks(Math.max(1, Number(e.target.value)))} fullWidth />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)} disabled={saveLoading}>{t('btn.cancel')}</Button>
            <Button onClick={handleSaveSession} variant="contained" color="primary" disabled={saveLoading}>
              {saveLoading ? <CircularProgress size={18} color="inherit" /> : 'Enregistrer la Séance'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── PRINT POPUP : filtres + chargement serveur + impression ── */}
        <Dialog open={printOpen} onClose={() => setPrintOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle className="no-print">{t('academy.scheduler.print_title')}</DialogTitle>
          <DialogContent dividers>
            <Box className="no-print" sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField type="date" fullWidth size="small" label={t('academy.scheduler.start_date')} value={printStartDate}
                    onChange={(e) => { setPrintStartDate(e.target.value); setPrintLoaded(false); }} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField type="date" fullWidth size="small" label={t('academy.scheduler.end_date')} value={printEndDate}
                    onChange={(e) => { setPrintEndDate(e.target.value); setPrintLoaded(false); }} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <TextField select fullWidth size="small" label={t('academy.scheduler.group_by_room')} value={printRoomId}
                    onChange={(e) => { setPrintRoomId(e.target.value === 'all' ? 'all' : Number(e.target.value)); setPrintLoaded(false); }}>
                    <MenuItem value="all">{t('academy.scheduler.all_rooms')}</MenuItem>
                    {rooms.map(r => (<MenuItem key={r.id} value={r.id}>{r.room_number}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                  <TextField select fullWidth size="small" label={t('academy.scheduler.group_by_teacher')} value={printTeacherId}
                    onChange={(e) => { setPrintTeacherId(e.target.value === 'all' ? 'all' : Number(e.target.value)); setPrintLoaded(false); }}>
                    <MenuItem value="all">{t('academy.scheduler.all_teachers')}</MenuItem>
                    {teachers.map(tt => (<MenuItem key={tt.id} value={tt.id}>{tt.full_name}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={1.7}>
                  <TextField select fullWidth size="small" label={t('academy.scheduler.group_by_group')} value={printGroupId}
                    onChange={(e) => { setPrintGroupId(e.target.value === 'all' ? 'all' : Number(e.target.value)); setPrintLoaded(false); }}>
                    <MenuItem value="all">{t('academy.scheduler.all_groups')}</MenuItem>
                    {groups.map(g => (<MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={1.5}>
                  <Button variant="outlined" fullWidth disabled={printLoading} onClick={handleLoadAndPreparePrint}>
                    {printLoading ? <CircularProgress size={18} color="inherit" /> : t('academy.scheduler.load')}
                  </Button>
                </Grid>
                <Grid item xs={12} md={1.5}>
                  <Button variant="contained" color="primary" fullWidth disabled={!printLoaded} startIcon={<PrintIcon />} onClick={() => window.print()}>
                    {t('academy.scheduler.print')}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Zone imprimable : en-tête agence + liste des séances */}
            <div id="printable-planning">
              {printLoaded ? (
                <>
                  {/* boubaker 22/08/2026 - en-tête édition : logo + coordonnées agence connectée + mois du planning */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
                    <tbody>
                      <tr>
                        <td style={{ width: 90, verticalAlign: 'middle' }}>
                          {user?.dosLogo ? (
                            <img src={user.dosLogo} alt="logo" style={{ maxWidth: 80, maxHeight: 80, objectFit: 'contain' }} />
                          ) : null}
                        </td>
                        <td style={{ verticalAlign: 'middle', fontSize: 11 }}>
                          <strong style={{ fontSize: 14 }}>{user?.dosLib || user?.agency || ''}</strong><br />
                          {user?.dosAdr1 ? (<>{user.dosAdr1}<br /></>) : null}
                          {user?.dosEmail ? <>Email : {user.dosEmail}<br /></> : null}
                          {user?.dosTelph ? <>Tél : {user.dosTelph}</> : null}
                        </td>
                        <td style={{ verticalAlign: 'middle', textAlign: 'right', fontSize: 12 }}>
                          {/* boubaker 22/08/2026 - titre du planning : "Planning Août 2026" */}
                          <strong style={{ fontSize: 15 }}>
                            {'Planning '}
                            {capitalizeFirst(new Date(printStartDate + 'T00:00:00').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }))}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {renderPrintList({
                    sessions: [...printSessions].sort((a, b) =>
                      a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time)),
                    rooms,
                    teachers,
                    groups,
                    t
                  })}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  {t('academy.scheduler.not_loaded')}
                </Typography>
              )}
            </div>
          </DialogContent>
          <DialogActions className="no-print">
            <Button onClick={() => setPrintOpen(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </GenericPage>
  );
};

// ── HELPER RENDERER FOR MATRIX CELLS (MONTH, WEEK, DAY) ──
interface RenderCellsParams {
  entityType: 'room' | 'group' | 'teacher';
  entityId: number;
  viewMode: 'day' | 'week' | 'month';
  monthDates: { dateStr: string; dayNum: number; shortDayName: string }[];
  weekDates: { dateStr: string; dayNum: number; shortDayName: string }[];
  timeSlots: { start: string; end: string; label: string }[];
  dateAnchor: string;
  sessions: Session[];
  groups: Group[];
  teachers: Teacher[];
  rooms: Room[];
  groupStudents: any[];
  pWeekendDays?: number[];
  pWeekendBgColor?: string;
  closedDateStr?: string;
  t: (key: string) => string;
  theme: any;
  onDragStartSession: (e: React.DragEvent, s: Session) => void;
  onCellDrop: (e: React.DragEvent, target: { dateStr?: string; slotStart?: string; slotEnd?: string }) => void;
}

function renderEntityCells(params: RenderCellsParams) {
  const {
    entityType, entityId, viewMode, monthDates, weekDates, timeSlots, dateAnchor,
    sessions, groups, teachers, rooms, pWeekendDays = [5, 6], pWeekendBgColor = '#fff7ed',
    closedDateStr, t, theme,
    onDragStartSession, onCellDrop
  } = params;

  const isDark = theme.palette.mode === 'dark';
  const todayStr = formatDateLocal(new Date());

  const dropProps = (target: { dateStr?: string; slotStart?: string; slotEnd?: string }) => ({
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); },
    onDrop: (e: React.DragEvent) => onCellDrop(e, target)
  });

  const sessionMatches = (s: Session, entityMatch: boolean, dateMatch: boolean): boolean => {
    if (!entityMatch) return false;
    return dateMatch;
  };

  // Render Dates array for Month or Week
  const targetDates = viewMode === 'month' ? monthDates : viewMode === 'week' ? weekDates : [];

  if (viewMode === 'month' || viewMode === 'week') {
    return targetDates.map(d => {
      const isClosed = !!closedDateStr && d.dateStr <= closedDateStr;
      const dayOfWeek = getDayOfWeekFromStr(d.dateStr);

      const matchedSessions = sessions.filter(s => {
        const entityMatch =
          (entityType === 'room' && s.room_id === entityId) ||
          (entityType === 'group' && s.group_id === entityId) ||
          (entityType === 'teacher' && s.teacher_id === entityId);

        if (s.date === d.dateStr) return sessionMatches(s, entityMatch, true);
        if (s.is_recurring) {
          const sDay = s.day_of_week != null ? Number(s.day_of_week) : getDayOfWeekFromStr(s.date);
          return sessionMatches(s, entityMatch, sDay === dayOfWeek);
        }
        return false;
      });

      const isToday = d.dateStr === todayStr;
      const isWeekend = pWeekendDays.includes(dayOfWeek);

      const cellBg = isClosed
        ? (isDark ? alpha('#334155', 0.35) : '#f1f5f9')
        : isToday
          ? (isDark ? alpha(theme.palette.primary.main, 0.08) : '#f0f9ff')
          : isWeekend
            ? (isDark ? alpha(pWeekendBgColor || '#f97316', 0.08) : '#fffbf5')
            : 'transparent';

      return (
        <td key={d.dateStr} {...dropProps({ dateStr: d.dateStr })} style={{
          padding: 6, verticalAlign: 'top',
          borderRight: '1px solid ' + theme.palette.divider,
          backgroundColor: cellBg
        }}>
          {matchedSessions.length > 0 ? (
            <Stack spacing={0.5}>
              {matchedSessions.map(s => {
                const groupName = groups.find(g => g.id === s.group_id)?.name || 'Groupe';
                const teacherName = teachers.find(tt => tt.id === s.teacher_id)?.full_name || 'Enseignant';
                const roomNum = rooms.find(r => r.id === s.room_id)?.room_number || 'Salle';

                let displayLabel = groupName;
                if (entityType === 'group') displayLabel = `${roomNum} (${teacherName})`;
                if (entityType === 'teacher') displayLabel = `${groupName} (${roomNum})`;

                const isSessionClosed = isClosed || (!!closedDateStr && s.date <= closedDateStr);
                const isCancelled = s.status === 'Cancelled';
                const isPostponed = s.status === 'Postponed';
                const isCompleted = s.status === 'Completed';

                const cardBg = isSessionClosed
                  ? (isDark ? '#475569' : '#64748b')
                  : isCancelled
                    ? (isDark ? '#7f1d1d' : '#dc2626')
                    : isPostponed
                      ? '#d97706'
                      : (s.attendance_taken || isCompleted ? '#059669' : '#6366f1');

                return (
                  <Tooltip key={s.id} title={`${s.start_time} - ${s.end_time} | ${groupName} - ${teacherName} - ${roomNum}${s.status && s.status !== 'Scheduled' ? ` [${s.status}]` : ''}${isSessionClosed ? ' [CLÔTURÉ]' : ''}`} arrow>
                    <Box
                      draggable={!isSessionClosed && !isCancelled}
                      onDragStart={(e: React.DragEvent) => !isSessionClosed && !isCancelled && onDragStartSession(e, s)}
                      onClick={() => window.dispatchEvent(new CustomEvent('blobtic-open-session-details', { detail: s }))}
                      sx={{
                        p: 1, borderRadius: 2, maxWidth: '100%', overflow: 'hidden',
                        bgcolor: cardBg,
                        color: 'white', cursor: isSessionClosed ? 'pointer' : (isCancelled ? 'pointer' : 'grab'), textAlign: 'left',
                        transition: 'all 0.15s',
                        border: isCancelled ? '1px dashed #fca5a5' : (isPostponed ? '1px dashed #fde68a' : 'none'),
                        opacity: isCancelled ? 0.85 : 1,
                        '&:hover': { transform: isSessionClosed ? 'none' : 'scale(1.03)', boxShadow: 2 },
                        '&:active': { cursor: isSessionClosed ? 'pointer' : 'grabbing' }
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" fontWeight="bold" display="block" noWrap sx={{ textDecoration: isCancelled ? 'line-through' : 'none' }}>
                          {s.start_time} - {s.end_time}
                        </Typography>
                        <Stack direction="row" spacing={0.3} alignItems="center">
                          {s.teacher_absent && (
                            <Chip label="Abs." size="small" sx={{ height: 14, fontSize: '0.55rem', bgcolor: '#991b1b', color: 'white', fontWeight: 'bold' }} />
                          )}
                          {isCancelled && (
                            <Chip label={t('academy.scheduler.status.cancelled')} size="small" sx={{ height: 14, fontSize: '0.55rem', bgcolor: 'rgba(0,0,0,0.35)', color: 'white', fontWeight: 'bold' }} />
                          )}
                          {isPostponed && (
                            <Chip label={t('academy.scheduler.status.postponed')} size="small" sx={{ height: 14, fontSize: '0.55rem', bgcolor: 'rgba(0,0,0,0.35)', color: 'white', fontWeight: 'bold' }} />
                          )}
                          {isSessionClosed && <LockIcon sx={{ fontSize: '0.75rem', opacity: 0.8 }} />}
                        </Stack>
                      </Box>
                      <Typography variant="caption" noWrap display="block" sx={{ opacity: 0.9, textDecoration: isCancelled ? 'line-through' : 'none' }}>
                        {displayLabel}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          ) : isClosed ? (
            <Box sx={{
              p: 1, borderRadius: 2, border: '1px dashed ' + theme.palette.divider,
              opacity: 0.6, bgcolor: isDark ? alpha(theme.palette.common.white, 0.01) : '#f8fafc',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              color: 'text.disabled', cursor: 'not-allowed'
            }}>
              <LockIcon sx={{ fontSize: 13 }} />
              <Typography variant="caption" fontWeight={600}>{t('academy.scheduler.closed_chip') || 'Clôturé'}</Typography>
            </Box>
          ) : (
            /* + Book Slot Placeholder */
            <Box
              onClick={() => window.dispatchEvent(new CustomEvent('blobtic-open-add-session', {
                detail: {
                  roomId: entityType === 'room' ? entityId : undefined,
                  teacherId: entityType === 'teacher' ? entityId : undefined,
                  groupId: entityType === 'group' ? entityId : undefined,
                  slotStart: '09:00', slotEnd: '10:30', targetDate: d.dateStr
                }
              }))}
              sx={{
                p: 1.2,
                borderRadius: 2,
                border: isToday ? '1px dashed #60a5fa' : isWeekend ? '1px dashed #fdba74' : '1px dashed ' + theme.palette.divider,
                cursor: 'pointer',
                color: isToday ? theme.palette.primary.main : isWeekend ? theme.palette.warning.main : 'text.secondary',
                bgcolor: isDark ? alpha(theme.palette.common.white, 0.02) : '#ffffff',
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                '&:hover': { bgcolor: isDark ? alpha(theme.palette.common.white, 0.06) : '#f1f5f9', borderColor: theme.palette.primary.main, color: theme.palette.primary.main }
              }}
            >
              <Typography variant="caption" fontWeight={600}>{t('academy.scheduler.book_slot')}</Typography>
            </Box>
          )}
        </td>
      );
    });
  }

  // ── DAY VIEW (Columns are Time Slots) ──
  const isDayClosed = !!closedDateStr && dateAnchor <= closedDateStr;

  return timeSlots.map(slot => {
    const dayOfWeek = getDayOfWeekFromStr(dateAnchor);

    const bookedSession = sessions.find(s => {
      const entityMatch =
        (entityType === 'room' && s.room_id === entityId) ||
        (entityType === 'group' && s.group_id === entityId) ||
        (entityType === 'teacher' && s.teacher_id === entityId);
      if (!entityMatch) return false;

      if (s.date === dateAnchor) {
        return (s.start_time <= slot.start && s.end_time > slot.start) ||
               (s.start_time >= slot.start && s.start_time < slot.end);
      }
      if (s.is_recurring) {
        const sDay = s.day_of_week != null ? Number(s.day_of_week) : getDayOfWeekFromStr(s.date);
        if (sDay === dayOfWeek) {
          return (s.start_time <= slot.start && s.end_time > slot.start) ||
                 (s.start_time >= slot.start && s.start_time < slot.end);
        }
      }
      return false;
    });

    if (bookedSession) {
      const groupName = groups.find(g => g.id === bookedSession.group_id)?.name || 'Groupe';
      const teacherName = teachers.find(tt => tt.id === bookedSession.teacher_id)?.full_name || 'Enseignant';
      const roomNum = rooms.find(r => r.id === bookedSession.room_id)?.room_number || 'Salle';
      const isSessionClosed = isDayClosed || (!!closedDateStr && bookedSession.date <= closedDateStr);
      const isDayCancelled = bookedSession.status === 'Cancelled';
      const isDayPostponed = bookedSession.status === 'Postponed';
      const isDayCompleted = bookedSession.status === 'Completed';

      const dayCardBg = isSessionClosed
        ? (isDark ? '#475569' : '#64748b')
        : isDayCancelled
          ? (isDark ? '#7f1d1d' : '#dc2626')
          : isDayPostponed
            ? '#d97706'
            : (bookedSession.attendance_taken || isDayCompleted ? '#059669' : '#6366f1');

      return (
        <td key={slot.start} {...dropProps({ dateStr: dateAnchor, slotStart: slot.start, slotEnd: slot.end })} style={{ padding: 6, borderRight: '1px solid ' + theme.palette.divider, backgroundColor: isDayClosed ? (isDark ? alpha('#334155', 0.35) : '#f1f5f9') : 'transparent' }}>
          <Box
            draggable={!isSessionClosed && !isDayCancelled}
            onDragStart={(e: React.DragEvent) => !isSessionClosed && !isDayCancelled && onDragStartSession(e, bookedSession)}
            onClick={() => window.dispatchEvent(new CustomEvent('blobtic-open-session-details', { detail: bookedSession }))}
            sx={{
              p: 1.5, borderRadius: 2, maxWidth: '100%', overflow: 'hidden',
              bgcolor: dayCardBg,
              color: 'white', cursor: isSessionClosed ? 'pointer' : (isDayCancelled ? 'pointer' : 'grab'),
              transition: 'transform 0.15s',
              border: isDayCancelled ? '1px dashed #fca5a5' : (isDayPostponed ? '1px dashed #fde68a' : 'none'),
              opacity: isDayCancelled ? 0.85 : 1,
              '&:hover': { transform: isSessionClosed ? 'none' : 'scale(1.03)', boxShadow: 3 },
              '&:active': { cursor: isSessionClosed ? 'pointer' : 'grabbing' }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ textDecoration: isDayCancelled ? 'line-through' : 'none' }}>
                {groupName}
              </Typography>
              <Stack direction="row" spacing={0.3} alignItems="center">
                {bookedSession.teacher_absent && (
                  <Chip label="Abs." size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#991b1b', color: 'white', fontWeight: 'bold' }} />
                )}
                {isDayCancelled && (
                  <Chip label={t('academy.scheduler.status.cancelled')} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(0,0,0,0.35)', color: 'white', fontWeight: 'bold' }} />
                )}
                {isDayPostponed && (
                  <Chip label={t('academy.scheduler.status.postponed')} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(0,0,0,0.35)', color: 'white', fontWeight: 'bold' }} />
                )}
                {isSessionClosed && <LockIcon sx={{ fontSize: '0.9rem', opacity: 0.8 }} />}
              </Stack>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.9, textDecoration: isDayCancelled ? 'line-through' : 'none' }} display="block" noWrap>
              {teacherName} ({roomNum})
            </Typography>
          </Box>
        </td>
      );
    }

    if (isDayClosed) {
      return (
        <td key={slot.start} {...dropProps({ dateStr: dateAnchor, slotStart: slot.start, slotEnd: slot.end })} style={{ padding: 6, borderRight: '1px solid ' + theme.palette.divider, backgroundColor: isDark ? alpha('#334155', 0.35) : '#f1f5f9' }}>
          <Box sx={{
            p: 2, borderRadius: 2, border: '1px dashed ' + theme.palette.divider,
            opacity: 0.6, bgcolor: isDark ? alpha(theme.palette.common.white, 0.01) : '#f8fafc',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            color: 'text.disabled', cursor: 'not-allowed'
          }}>
            <LockIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" fontWeight={600}>{t('academy.scheduler.closed_chip') || 'Clôturé'}</Typography>
          </Box>
        </td>
      );
    }

    return (
      <td key={slot.start} {...dropProps({ dateStr: dateAnchor, slotStart: slot.start, slotEnd: slot.end })} style={{ padding: 6, borderRight: '1px solid ' + theme.palette.divider }}>
        <Box
          onClick={() => window.dispatchEvent(new CustomEvent('blobtic-open-add-session', {
            detail: {
              roomId: entityType === 'room' ? entityId : undefined,
              teacherId: entityType === 'teacher' ? entityId : undefined,
              groupId: entityType === 'group' ? entityId : undefined,
              slotStart: slot.start, slotEnd: slot.end, targetDate: dateAnchor
            }
          }))}
          sx={{
            p: 2, borderRadius: 2, border: '1px dashed ' + theme.palette.divider, cursor: 'pointer', color: 'text.secondary',
            bgcolor: isDark ? alpha(theme.palette.common.white, 0.02) : '#ffffff',
            transition: 'all 0.2s', '&:hover': { bgcolor: isDark ? alpha(theme.palette.common.white, 0.06) : '#f1f5f9', borderColor: theme.palette.primary.main, color: theme.palette.primary.main }
          }}
        >
          <Typography variant="caption" fontWeight={600}>{t('academy.scheduler.book_slot')}</Typography>
        </Box>
      </td>
    );
  });
}

// ── IMPRESSION : liste plate des séances (Jour | Début | Fin | Salle | Enseignant | Groupe) ──
// boubaker 22/08/2026 - colonne 1 = jour (ex: DIM 16), puis heures, salle, enseignant...
const PRINT_DAY_SHORT = ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];

interface PrintListParams {
  sessions: Session[];
  rooms: Room[];
  teachers: Teacher[];
  groups: Group[];
  t: (key: string) => string;
}

function renderPrintList(params: PrintListParams) {
  const { sessions, rooms, teachers, groups, t } = params;

  return (
    <table className="print-sessions-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
      <thead>
        <tr style={{ backgroundColor: '#f0f0f0' }}>
          <th style={printCellStyle(110)}>{t('academy.scheduler.print.col_day')}</th>
          <th style={printCellStyle(60)}>{t('academy.scheduler.print.col_start')}</th>
          <th style={printCellStyle(60)}>{t('academy.scheduler.print.col_end')}</th>
          <th style={printCellStyle()}>{t('academy.scheduler.print.col_room')}</th>
          <th style={printCellStyle()}>{t('academy.scheduler.print.col_teacher')}</th>
          <th style={printCellStyle()}>{t('academy.scheduler.print.col_group')}</th>
        </tr>
      </thead>
      <tbody>
        {sessions.length === 0 ? (
          <tr><td colSpan={6} style={{ ...printCellStyle(), textAlign: 'center' }}>—</td></tr>
        ) : sessions.map(s => {
          const dateObj = new Date(s.date + 'T00:00:00');
          // boubaker 22/08/2026 - format "DIM 02 Août" : jour + n° sur 2 chiffres + mois
          const dayLabel = `${PRINT_DAY_SHORT[dateObj.getDay()] || ''} `
            + `${String(dateObj.getDate()).padStart(2, '0')} `
            + `${capitalizeFirst(dateObj.toLocaleDateString('fr-FR', { month: 'long' }))}`;
          return (
            <tr key={s.id}>
              <td style={printCellStyle(110, { fontWeight: 'bold' })}>{dayLabel}</td>
              <td style={printCellStyle(60)}>{s.start_time}</td>
              <td style={printCellStyle(60)}>{s.end_time}</td>
              <td style={printCellStyle()}>{rooms.find(r => r.id === s.room_id)?.room_number || ''}</td>
              <td style={printCellStyle()}>{teachers.find(tt => tt.id === s.teacher_id)?.full_name || ''}</td>
              <td style={printCellStyle()}>{groups.find(g => g.id === s.group_id)?.name || ''}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function printCellStyle(width?: number, extra?: React.CSSProperties): React.CSSProperties {
  return { border: '1px solid #999', padding: '3px 5px', textAlign: 'left', width, ...extra };
}

// boubaker 22/08/2026 - "août 2026" → "Août 2026" (toLocaleDateString renvoie tout en minuscules)
function capitalizeFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export default SchedulerPage;
